import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/format";
import { ResetPasswordDialog } from "@/components/PasswordDialog";
import { Building2, MapPin, Users, UserRound, Wallet, TrendingUp, ArrowLeft, KeyRound, Mail, Phone, Calendar } from "lucide-react";

export default function BranchDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/branches/${id}/details`).then((r) => setD(r.data)); }, [id]);

  if (!d) return <div className="text-muted-foreground py-20 text-center">Loading branch…</div>;
  const b = d.branch;

  return (
    <div className="animate-fade-up space-y-6" data-testid="branch-details-page">
      <button onClick={() => navigate("/branches")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary row-hover"><ArrowLeft className="h-4 w-4" /> Back to Branches</button>

      <div className="card-surface rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Building2 className="h-7 w-7" /></div>
            <div>
              <div className="font-display text-2xl font-bold">{b.name} Branch</div>
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><MapPin className="h-3.5 w-3.5" /> {b.city}, {b.state} · <span className="num">{b.code}</span></div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                {b.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {b.email}</span>}
                {b.contact && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {b.contact}</span>}
                {b.created_at && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {String(b.created_at).slice(0, 10)}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${b.active ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>{b.active ? "Active" : "Inactive"}</span>
            {b.admin && (user.role === "super_admin" || user.role === "admin") && (
              <ResetPasswordDialog userId={b.admin.id} userName={b.admin.name}
                trigger={<button data-testid="branch-reset-password" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:border-primary/40 row-hover"><KeyRound className="h-3.5 w-3.5" /> Reset Admin Password</button>} />
            )}
          </div>
        </div>
        {b.admin && <div className="mt-4 text-xs text-muted-foreground">Branch Admin: <span className="text-foreground font-medium">{b.admin.name}</span> · {b.admin.email}</div>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SumCard testid="bd-managers" label="Managers" value={d.manager_count} icon={Users} />
        <SumCard testid="bd-clients" label="Clients" value={d.client_count} icon={UserRound} onClick={() => navigate(`/clients?branch=${id}`)} />
        <SumCard testid="bd-aum" label="Total Assets / AUM" value={formatINR(d.total_aum)} icon={Wallet} onClick={() => navigate(`/clients?branch=${id}`)} />
        <SumCard testid="bd-business" label="Total Business" value={formatINR(d.total_business)} icon={TrendingUp} />
      </div>

      <div className="card-surface rounded-xl p-5">
        <h3 className="font-display font-semibold mb-4">Product-wise Business</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bd-product-grid">
          {d.product_business.map((p) => (
            <button key={p.key} onClick={() => navigate(`/clients?branch=${id}&product=${p.key}`)} data-testid={`bd-product-${p.key}`} className="text-left rounded-lg border border-border p-4 row-hover hover:border-primary/40 hover:bg-secondary">
              <div className="text-sm font-medium">{p.label}</div>
              <div className="mt-1 font-display text-lg font-bold num text-primary">{formatINR(p.current)}</div>
              <div className="text-xs text-muted-foreground num">{p.clients} clients · {p.count} holdings</div>
            </button>
          ))}
          {d.product_business.length === 0 && <div className="text-sm text-muted-foreground">No holdings yet.</div>}
        </div>
      </div>

      <div className="card-surface rounded-xl p-5">
        <h3 className="font-display font-semibold mb-4">Managers ({d.managers.length})</h3>
        <div className="space-y-2" data-testid="bd-managers-list">
          {d.managers.map((m) => (
            <div key={m.id} onClick={() => navigate(`/managers/${m.id}`)} data-testid={`bd-manager-${m.id}`} className="flex items-center gap-4 py-2.5 px-2 border-b border-border/40 last:border-0 cursor-pointer row-hover hover:bg-secondary rounded-lg">
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.name}{!m.active && <span className="ml-2 text-xs text-destructive">Inactive</span>}</div><div className="text-xs text-muted-foreground truncate">{m.email}</div></div>
              <button onClick={(e) => { e.stopPropagation(); navigate(`/clients?manager=${m.id}`); }} className="text-right hover:text-primary"><div className="text-sm num font-medium">{m.clients}</div><div className="text-[10px] text-muted-foreground uppercase">Clients</div></button>
              <div className="text-right w-24"><div className="text-sm font-semibold num text-primary">{formatINR(m.aum)}</div><div className="text-[10px] text-muted-foreground uppercase">AUM</div></div>
            </div>
          ))}
          {d.managers.length === 0 && <div className="text-sm text-muted-foreground py-4">No managers in this branch.</div>}
        </div>
      </div>

      <div className="card-surface rounded-xl p-5">
        <h3 className="font-display font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-2">
          {d.recent_activities.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-border/30 last:border-0">
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary capitalize">{a.type}</span>
              <span className="font-medium">{a.client_name}</span>
              <span className="text-muted-foreground truncate flex-1">{a.note}</span>
              <span className="text-xs text-muted-foreground num">{String(a.date).slice(0, 10)}</span>
            </div>
          ))}
          {d.recent_activities.length === 0 && <div className="text-sm text-muted-foreground">No recent activity.</div>}
        </div>
      </div>
    </div>
  );
}

function SumCard({ label, value, icon: Icon, onClick, testid }) {
  return (
    <div onClick={onClick} data-testid={testid} className={`card-surface rounded-xl p-5 stat-glow ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div><div className="text-xs text-muted-foreground font-medium">{label}</div><div className="mt-2 font-display text-2xl font-bold num">{value}</div></div>
        <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}
